import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard, Package, ShoppingCart, Users, Monitor, Landmark, FileText,
  GraduationCap, Crown, Heart, Video, Zap, Share2, Store, Send, Tag, Gift,
  Boxes, Rocket, Target, Globe, HelpCircle, Bell, Settings, UserCog, BarChart3,
  Activity, Key, BookOpen, Webhook, Search, ChevronDown, ChevronRight, Truck,
  Clock, DollarSign, Sliders, Layout, Palette, Inbox, History, Sparkles,
  TrendingUp, Warehouse, MessageSquare, Award, ShieldCheck, MousePointerClick,
  PieChart, CalendarClock, ClipboardCheck
} from 'lucide-react';

const sections = [
  {
    id: 'getting-started',
    title: '🚀 Memulai',
    description: 'Langkah awal menggunakan Tokomoe',
    items: [
      {
        title: 'Dashboard',
        icon: LayoutDashboard,
        route: '/dashboard',
        color: '#3B82F6',
        description: 'Halaman utama yang menampilkan ringkasan toko Anda.',
        steps: [
          'Lihat total pesanan, pendapatan, dan pelanggan hari ini',
          'Pantau grafik penjualan harian/mingguan/bulanan',
          'Lihat pesanan terbaru dan produk terlaris',
          'Klik angka/kartu untuk masuk ke detail masing-masing'
        ],
        tips: 'Dashboard otomatis refresh setiap kali Anda buka. Gunakan filter tanggal untuk melihat performa periode tertentu.'
      },
      {
        title: 'Analytics',
        icon: PieChart,
        route: '/dashboard/analytics',
        color: '#8B5CF6',
        description: 'Analisis mendalam performa toko Anda.',
        steps: [
          'Lihat grafik penjualan, konversi, dan traffic',
          'Analisis produk terlaris dan kategori populer',
          'Pantau sumber traffic pengunjung',
          'Export data untuk analisis lebih lanjut'
        ],
        tips: 'Gunakan perbandingan periode untuk melihat tren pertumbuhan toko Anda.'
      },
    ]
  },
  {
    id: 'products',
    title: '📦 Produk & Inventori',
    description: 'Kelola produk, stok, dan serial key',
    items: [
      {
        title: 'Produk',
        icon: Package,
        route: '/dashboard/products',
        color: '#10B981',
        description: 'Kelola semua produk toko Anda — fisik, digital, kursus, membership, donasi.',
        steps: [
          'Klik "Tambah Produk" untuk membuat produk baru',
          'Pilih tipe produk: Fisik, Digital, Kursus, Membership, atau Donasi',
          'Isi nama, deskripsi, harga, dan upload gambar produk',
          'Untuk produk fisik: isi berat dan stok',
          'Untuk produk digital: upload file atau isi link download',
          'Aktifkan produk agar tampil di storefront',
          'Gunakan fitur duplikat untuk membuat produk serupa dengan cepat'
        ],
        tips: 'Pastikan foto produk berkualitas tinggi dan deskripsi lengkap untuk meningkatkan konversi.'
      },
      {
        title: 'Inventori',
        icon: Warehouse,
        route: '/dashboard/inventory',
        color: '#F59E0B',
        description: 'Kelola stok dan pantau pergerakan inventori.',
        steps: [
          'Lihat stok semua produk dalam satu tabel',
          'Set stok minimum untuk notifikasi stok rendah',
          'Lakukan penyesuaian stok manual (tambah/kurang)',
          'Lihat riwayat perubahan stok'
        ],
        tips: 'Aktifkan notifikasi stok rendah di menu Integrasi agar Anda mendapat alert via WhatsApp saat stok menipis.'
      },
      {
        title: 'Serial Key',
        icon: Key,
        route: '/dashboard/serial-keys',
        color: '#EF4444',
        description: 'Kelola serial number/license key untuk produk digital.',
        steps: [
          'Pilih produk yang ingin ditambahkan serial key',
          'Input serial key satu per satu atau bulk import',
          'Serial key otomatis dikirim ke pembeli setelah pembayaran dikonfirmasi',
          'Pantau status serial key: tersedia, terpakai, expired'
        ],
        tips: 'Gunakan fitur bulk import untuk menambahkan banyak serial key sekaligus dari file CSV.'
      },
      {
        title: 'Bundling',
        icon: Boxes,
        route: '/dashboard/bundles',
        color: '#8B5CF6',
        description: 'Buat paket bundling produk dengan harga spesial.',
        steps: [
          'Klik "Buat Bundle" dan beri nama paket',
          'Pilih produk-produk yang ingin dibundel',
          'Set harga bundle (biasanya lebih murah dari total harga satuan)',
          'Aktifkan bundle agar tampil di storefront dan LP',
          'Bundle juga bisa digunakan di Landing Page Checkout'
        ],
        tips: 'Bundle dengan diskon 20-30% dari harga satuan adalah sweet spot untuk meningkatkan average order value.'
      },
    ]
  },
  {
    id: 'orders',
    title: '🛒 Pesanan & Pelanggan',
    description: 'Kelola pesanan, pelanggan, dan tiket support',
    items: [
      {
        title: 'Pesanan',
        icon: ShoppingCart,
        route: '/dashboard/orders',
        color: '#3B82F6',
        description: 'Kelola semua pesanan masuk dari semua channel.',
        steps: [
          'Lihat daftar pesanan dengan filter status: Pending, Paid, Shipped, Completed',
          'Klik pesanan untuk melihat detail lengkap',
          'Update status pesanan (konfirmasi, kirim, selesai)',
          'Input nomor resi untuk pesanan produk fisik',
          'Batalkan pesanan jika diperlukan',
          'Cetak invoice langsung dari detail pesanan'
        ],
        tips: 'Pesanan pending yang belum dibayar akan otomatis diingatkan via follow-up jika fitur follow-up aktif.'
      },
      {
        title: 'Pelanggan',
        icon: Users,
        route: '/dashboard/customers',
        color: '#06B6D4',
        description: 'Database semua pelanggan toko Anda.',
        steps: [
          'Lihat daftar pelanggan beserta total belanja mereka',
          'Klik pelanggan untuk melihat riwayat pesanan',
          'Filter berdasarkan status, tanggal, atau total belanja',
          'Export data pelanggan ke CSV',
          'Lihat pelanggan yang paling banyak berbelanja'
        ],
        tips: 'Gunakan fitur Segmentasi untuk mengelompokkan pelanggan dan kirim pesan yang lebih personal.'
      },
      {
        title: 'Tiket Support',
        icon: MessageSquare,
        route: '/dashboard/support-tickets',
        color: '#F97316',
        description: 'Sistem tiket untuk menangani pertanyaan dan komplain pelanggan.',
        steps: [
          'Pelanggan bisa membuat tiket dari area member mereka',
          'Balas tiket langsung dari dashboard',
          'Set prioritas: Low, Medium, High, Urgent',
          'Tandai tiket sebagai Resolved saat selesai',
          'Lihat statistik response time dan kepuasan pelanggan'
        ],
        tips: 'Respon cepat (< 1 jam) meningkatkan kepercayaan pelanggan secara signifikan.'
      },
    ]
  },
  {
    id: 'pos',
    title: '🖥️ POS / Kasir',
    description: 'Sistem point-of-sale untuk penjualan offline',
    items: [
      {
        title: 'Kasir POS',
        icon: Monitor,
        route: '/dashboard/pos',
        color: '#059669',
        description: 'Interface kasir untuk penjualan langsung/toko fisik.',
        steps: [
          'Buka shift baru untuk memulai penjualan',
          'Cari dan tambahkan produk ke keranjang',
          'Pilih metode pembayaran: Cash, QRIS, Transfer',
          'Proses pembayaran dan cetak struk',
          'Tutup shift di akhir hari untuk rekap penjualan'
        ],
        tips: 'POS mendukung barcode scanner. Hubungkan scanner USB untuk input produk lebih cepat.'
      },
      {
        title: 'Riwayat Shift',
        icon: Clock,
        route: '/dashboard/pos-shifts',
        color: '#6366F1',
        description: 'Lihat riwayat semua shift kasir.',
        steps: [
          'Lihat semua shift yang pernah dibuka',
          'Klik shift untuk melihat detail transaksi',
          'Bandingkan uang tunai aktual vs sistem',
          'Lihat rekap per kasir/staff'
        ],
        tips: 'Selalu tutup shift dan hitung kas fisik untuk memastikan tidak ada selisih.'
      },
      {
        title: 'Laporan POS',
        icon: BarChart3,
        route: '/dashboard/pos-reports',
        color: '#0EA5E9',
        description: 'Laporan penjualan dari channel POS.',
        steps: [
          'Lihat total penjualan POS per hari/minggu/bulan',
          'Analisis produk terlaris di POS',
          'Bandingkan performa antar shift dan kasir',
          'Export laporan ke file'
        ],
        tips: 'Bandingkan data POS dengan online untuk melihat kontribusi masing-masing channel.'
      },
      {
        title: 'Pengaturan POS',
        icon: Settings,
        route: '/dashboard/pos-settings',
        color: '#64748B',
        description: 'Konfigurasi sistem POS.',
        steps: [
          'Set nama toko dan alamat untuk struk',
          'Konfigurasi metode pembayaran yang diterima',
          'Atur format struk dan printer',
          'Set staff yang boleh mengakses POS'
        ],
        tips: 'Tambahkan logo toko di pengaturan untuk tampil di struk yang lebih profesional.'
      },
    ]
  },
  {
    id: 'finance',
    title: '💰 Keuangan',
    description: 'Invoice, pembayaran, dan rekening bank',
    items: [
      {
        title: 'Invoice',
        icon: FileText,
        route: '/dashboard/invoices',
        color: '#3B82F6',
        description: 'Buat dan kelola invoice untuk pelanggan.',
        steps: [
          'Klik "Buat Invoice" untuk invoice baru',
          'Pilih pelanggan atau input data manual',
          'Tambahkan item, harga, dan diskon',
          'Set tanggal jatuh tempo',
          'Kirim invoice via WhatsApp atau Email',
          'Pantau status: Draft, Sent, Paid, Overdue'
        ],
        tips: 'Invoice otomatis berubah status ke Overdue ketika melewati tanggal jatuh tempo.'
      },
      {
        title: 'Payment Plan / Termin',
        icon: CalendarClock,
        route: '/dashboard/payment-plans',
        color: '#8B5CF6',
        description: 'Kelola pembayaran cicilan/termin.',
        steps: [
          'Lihat semua pembayaran termin yang aktif',
          'Pantau jatuh tempo cicilan berikutnya',
          'Konfirmasi pembayaran cicilan masuk',
          'Sistem otomatis kirim reminder sebelum jatuh tempo'
        ],
        tips: 'Aktifkan notifikasi termin di menu Template Notifikasi untuk reminder otomatis ke pelanggan.'
      },
      {
        title: 'Rekening Bank',
        icon: Landmark,
        route: '/dashboard/bank-accounts',
        color: '#059669',
        description: 'Kelola rekening bank untuk menerima pembayaran manual.',
        steps: [
          'Tambah rekening bank yang Anda miliki',
          'Isi nama bank, nomor rekening, dan atas nama',
          'Rekening ini akan ditampilkan ke pelanggan saat checkout manual',
          'Anda bisa punya beberapa rekening sekaligus'
        ],
        tips: 'Tambahkan minimal 2-3 bank berbeda agar pelanggan punya pilihan transfer yang sesuai.'
      },
      {
        title: 'Konfirmasi Transfer',
        icon: ClipboardCheck,
        route: '/dashboard/payment-confirmations',
        color: '#F59E0B',
        description: 'Konfirmasi pembayaran transfer manual dari pelanggan.',
        steps: [
          'Pelanggan upload bukti transfer dari halaman mereka',
          'Lihat daftar konfirmasi yang masuk',
          'Verifikasi bukti transfer dengan rekening bank',
          'Approve atau Reject konfirmasi',
          'Status pesanan otomatis berubah setelah approve'
        ],
        tips: 'Proses konfirmasi secepat mungkin untuk pengalaman pelanggan yang baik.'
      },
      {
        title: 'COD',
        icon: Truck,
        route: '/dashboard/cod',
        color: '#EF4444',
        description: 'Pengaturan Cash on Delivery.',
        steps: [
          'Aktifkan fitur COD untuk toko Anda',
          'Set biaya tambahan COD (opsional)',
          'Set area/kota yang bisa COD',
          'Pesanan COD otomatis masuk ke daftar pesanan'
        ],
        tips: 'COD cocok untuk produk fisik. Set biaya COD untuk menutup risiko retur.'
      },
      {
        title: 'High-Ticket',
        icon: DollarSign,
        route: '/dashboard/high-ticket',
        color: '#7C3AED',
        description: 'Kelola penjualan produk/jasa dengan harga tinggi.',
        steps: [
          'Lihat daftar order high-ticket',
          'Kelola pembayaran bertahap (DP + pelunasan)',
          'Track progress project/deliverable',
          'Kirim invoice per tahap pembayaran'
        ],
        tips: 'Untuk produk > Rp 5 juta, gunakan high-ticket mode dengan pembayaran bertahap.'
      },
    ]
  },
  {
    id: 'modules',
    title: '📚 Modul Bisnis',
    description: 'Kursus, membership, donasi, webinar, & PPOB',
    items: [
      {
        title: 'Kursus / LMS',
        icon: GraduationCap,
        route: '/dashboard/courses',
        color: '#F59E0B',
        description: 'Buat dan jual kursus online.',
        steps: [
          'Klik "Buat Kursus" untuk membuat kursus baru',
          'Isi judul, deskripsi, thumbnail, dan harga kursus',
          'Klik kursus untuk masuk ke Course Editor',
          'Tambahkan bab/section dan lesson di dalam bab',
          'Upload video, dokumen, atau teks sebagai konten lesson',
          'Buat quiz untuk mengukur pemahaman siswa',
          'Publish kursus agar bisa dibeli dan diakses',
          'Pantau progress dan skor siswa'
        ],
        tips: 'Buat kursus gratis sebagai lead magnet untuk menarik calon pelanggan.'
      },
      {
        title: 'Sertifikat',
        icon: Award,
        route: '/dashboard/certificates',
        color: '#10B981',
        description: 'Konfigurasi sertifikat untuk kursus dan webinar.',
        steps: [
          'Aktifkan fitur sertifikat',
          'Upload template sertifikat (gambar latar belakang)',
          'Set kriteria penerbitan: harus lulus quiz / selesai semua lesson',
          'Sertifikat otomatis digenerate saat siswa memenuhi syarat',
          'Siswa bisa download sertifikat dari area member'
        ],
        tips: 'Sertifikat meningkatkan perceived value kursus Anda. Desain yang profesional membuat siswa bangga membagikannya.'
      },
      {
        title: 'LMS Enhanced',
        icon: GraduationCap,
        route: '/dashboard/lms-enhanced',
        color: '#6366F1',
        description: 'Fitur LMS lanjutan: quiz, progress tracking, discussion.',
        steps: [
          'Lihat progress semua siswa per kursus',
          'Kelola quiz dan bank soal',
          'Lihat statistik completion rate',
          'Kelola discussion/forum per kursus'
        ],
        tips: 'Gunakan quiz di akhir setiap bab untuk memastikan siswa paham materi sebelum lanjut.'
      },
      {
        title: 'Membership',
        icon: Crown,
        route: '/dashboard/memberships',
        color: '#F59E0B',
        description: 'Buat program membership berlangganan.',
        steps: [
          'Klik "Buat Membership" untuk level baru',
          'Set nama tier (misal: Silver, Gold, Platinum)',
          'Set harga dan durasi (bulanan/tahunan)',
          'Tentukan benefit: diskon, akses kursus, produk eksklusif',
          'Member yang berlangganan otomatis mendapat akses sesuai tier',
          'Pantau member aktif dan yang akan expired'
        ],
        tips: 'Buat 3 tier membership — basic, standard, premium — dengan perbedaan benefit yang jelas.'
      },
      {
        title: 'Donasi',
        icon: Heart,
        route: '/dashboard/donations',
        color: '#EF4444',
        description: 'Kelola campaign donasi/galang dana.',
        steps: [
          'Klik "Buat Campaign" untuk campaign baru',
          'Isi judul, deskripsi, target dana, dan gambar',
          'Campaign otomatis tampil di storefront',
          'Donatur bisa berdonasi dengan nominal bebas',
          'Pantau progress donasi real-time',
          'Download daftar donatur untuk reporting'
        ],
        tips: 'Update progress campaign secara rutin untuk meningkatkan kepercayaan donatur.'
      },
      {
        title: 'Webinar',
        icon: Video,
        route: '/dashboard/webinars',
        color: '#7C3AED',
        description: 'Buat dan kelola webinar online.',
        steps: [
          'Klik "Buat Webinar" dan isi detail webinar',
          'Set judul, pembicara, jadwal, harga (atau gratis)',
          'Isi link meeting (Zoom/Google Meet)',
          'Publish agar peserta bisa mendaftar',
          'Kirim reminder sebelum webinar dimulai',
          'Saat webinar selesai, tandai kehadiran peserta',
          'Generate sertifikat untuk peserta yang hadir',
          'Kirim rekaman webinar ke semua peserta'
        ],
        tips: 'Isi field "Link Rekaman" di edit webinar, lalu klik "Kirim Rekaman" untuk blast ke semua peserta via WA & Email.'
      },
      {
        title: 'PPOB',
        icon: Zap,
        route: '/dashboard/ppob',
        color: '#F97316',
        description: 'Jual produk digital: pulsa, paket data, token listrik, dll.',
        steps: [
          'Aktifkan modul PPOB di pengaturan',
          'Konfigurasi provider PPOB (DigiFlazz)',
          'Set markup harga untuk setiap kategori',
          'Produk PPOB otomatis tampil di storefront',
          'Pelanggan bisa beli langsung, SN otomatis terkirim'
        ],
        tips: 'Set markup 1-5% dari harga modal untuk keuntungan yang masuk akal.'
      },
      {
        title: 'PPOB Enhanced',
        icon: Zap,
        route: '/dashboard/ppob-enhanced',
        color: '#EA580C',
        description: 'Fitur PPOB lanjutan dengan lebih banyak kategori dan pengaturan.',
        steps: [
          'Lihat semua kategori PPOB yang tersedia',
          'Atur markup per kategori produk',
          'Monitoring status transaksi PPOB',
          'Lihat laporan keuntungan PPOB'
        ],
        tips: 'Fokuskan pada kategori PPOB yang paling banyak diminta di area target Anda.'
      },
      {
        title: 'Projects (High-Ticket)',
        icon: DollarSign,
        route: '/dashboard/high-ticket-projects',
        color: '#0891B2',
        description: 'Kelola project dan deliverable untuk penjualan high-ticket.',
        steps: [
          'Buat project baru terkait order high-ticket',
          'Breakdown deliverable per milestone',
          'Track progress pengerjaan',
          'Upload hasil deliverable untuk client review',
          'Tandai milestone selesai untuk trigger pembayaran tahap berikutnya'
        ],
        tips: 'Gunakan milestone yang jelas agar client tahu progress pekerjaan secara transparan.'
      },
    ]
  },
  {
    id: 'marketing',
    title: '📢 Marketing',
    description: 'Affiliate, broadcast, voucher, follow-up, dan lainnya',
    items: [
      {
        title: 'Affiliate',
        icon: Share2,
        route: '/dashboard/affiliates',
        color: '#10B981',
        description: 'Program affiliate/referral untuk reseller/promoter.',
        steps: [
          'Aktifkan program affiliate',
          'Set persentase komisi per produk atau global',
          'Affiliate mendaftar dan mendapat link unik',
          'Setiap penjualan dari link affiliate akan tercatat',
          'Lihat daftar affiliate dan komisi mereka',
          'Proses pencairan komisi affiliate'
        ],
        tips: 'Komisi 10-20% untuk produk digital dan 5-10% untuk produk fisik adalah standar industri.'
      },
      {
        title: 'Reseller',
        icon: Store,
        route: '/dashboard/resellers',
        color: '#8B5CF6',
        description: 'Kelola program reseller dengan harga khusus.',
        steps: [
          'Buat tier reseller (misal: Agen, Distributor)',
          'Set harga reseller per produk per tier',
          'Berikan kode reseller ke setiap reseller',
          'Reseller input kode saat checkout untuk harga spesial',
          'Monitor penjualan per reseller'
        ],
        tips: 'Buat minimal 2-3 tier reseller dengan selisih harga yang menarik.'
      },
      {
        title: 'Broadcast',
        icon: Send,
        route: '/dashboard/broadcast',
        color: '#3B82F6',
        description: 'Kirim pesan massal ke pelanggan via WhatsApp.',
        steps: [
          'Pilih target: semua pelanggan atau segment tertentu',
          'Tulis pesan broadcast',
          'Gunakan variabel: {nama}, {email} untuk personalisasi',
          'Preview pesan sebelum kirim',
          'Kirim broadcast dan pantau status pengiriman',
          'Lihat laporan: terkirim, gagal, dibaca'
        ],
        tips: 'Kirim broadcast di jam 10-12 siang atau 19-21 malam untuk open rate tertinggi.'
      },
      {
        title: 'Follow-Up',
        icon: Clock,
        route: '/dashboard/followup',
        color: '#F59E0B',
        description: 'Lihat antrian pesan follow-up otomatis.',
        steps: [
          'Lihat semua pesan follow-up yang terjadwal',
          'Pesan dikirim otomatis sesuai trigger (order baru, belum bayar, dll)',
          'Pantau status: Pending, Sent, Failed',
          'Retry pesan yang gagal',
          'Kosongkan antrian jika diperlukan'
        ],
        tips: 'Follow-up 3x (1 jam, 24 jam, 48 jam setelah order) adalah formula yang paling efektif.'
      },
      {
        title: 'Abandoned Cart',
        icon: ShoppingCart,
        route: '/dashboard/abandoned-carts',
        color: '#EF4444',
        description: 'Recover pelanggan yang meninggalkan keranjang.',
        steps: [
          'Lihat daftar keranjang yang ditinggalkan',
          'Lihat detail produk yang hampir dibeli',
          'Kirim reminder manual atau otomatis via WA',
          'Track recovery rate — berapa yang akhirnya beli'
        ],
        tips: 'Tawarkan diskon kecil (5-10%) di pesan follow-up abandoned cart untuk meningkatkan konversi.'
      },
      {
        title: 'Atur Follow-Up',
        icon: MessageSquare,
        route: '/dashboard/followup-settings',
        color: '#F97316',
        description: 'Konfigurasi aturan follow-up otomatis.',
        steps: [
          'Aktifkan follow-up otomatis',
          'Set trigger: order baru, belum bayar, abandoned cart',
          'Set delay waktu pengiriman (misal: 1 jam setelah order)',
          'Tulis template pesan follow-up',
          'Aktifkan/nonaktifkan per trigger'
        ],
        tips: 'Jangan terlalu agresif — 3 follow-up sudah cukup. Lebih dari itu bisa mengganggu pelanggan.'
      },
      {
        title: 'Voucher & Promo',
        icon: Tag,
        route: '/dashboard/vouchers',
        color: '#EC4899',
        description: 'Buat kode voucher dan program promo.',
        steps: [
          'Klik "Buat Voucher" untuk voucher baru',
          'Set kode voucher (misal: DISKON10)',
          'Pilih tipe: persentase atau nominal tetap',
          'Set minimum order dan maksimum diskon',
          'Set tanggal berlaku dan kuota penggunaan',
          'Voucher bisa dipakai pelanggan saat checkout'
        ],
        tips: 'Buat voucher limited time (24-48 jam) untuk menciptakan urgency.'
      },
      {
        title: 'Order Bumps',
        icon: Zap,
        route: '/dashboard/order-bumps',
        color: '#F59E0B',
        description: 'Tambahkan produk tambahan di halaman checkout.',
        steps: [
          'Pilih produk utama (trigger)',
          'Pilih produk bump yang ditawarkan',
          'Set harga spesial untuk bump',
          'Tulis copy yang menarik untuk penawaran',
          'Bump akan muncul di halaman checkout sebagai centangan'
        ],
        tips: 'Produk bump yang paling efektif adalah yang melengkapi produk utama, dengan harga < 30% dari produk utama.'
      },
      {
        title: 'Upsell & Downsell',
        icon: TrendingUp,
        route: '/dashboard/upsell-funnels',
        color: '#7C3AED',
        description: 'Buat funnel upsell dan downsell setelah pembelian.',
        steps: [
          'Buat funnel baru dan beri nama',
          'Set produk trigger (produk yang dibeli)',
          'Tambahkan step upsell (produk lebih mahal/premium)',
          'Tambahkan step downsell (alternatif lebih murah jika upsell ditolak)',
          'Pelanggan akan melihat penawaran setelah checkout berhasil'
        ],
        tips: 'Upsell dengan diskon 30-50% memiliki acceptance rate tertinggi.'
      },
      {
        title: 'Loyalty & Poin',
        icon: Gift,
        route: '/dashboard/loyalty-settings',
        color: '#10B981',
        description: 'Program loyalitas dengan sistem poin.',
        steps: [
          'Aktifkan program loyalty',
          'Set aturan mendapat poin (misal: setiap Rp 10.000 = 1 poin)',
          'Set poin dari aktivitas lain (registrasi, review, dll)',
          'Poin bisa ditukar dengan diskon atau reward',
          'Pelanggan melihat saldo poin mereka di area member'
        ],
        tips: 'Berikan bonus poin ganda di event spesial untuk meningkatkan engagement.'
      },
      {
        title: 'Reward Catalog',
        icon: Gift,
        route: '/dashboard/loyalty-rewards',
        color: '#059669',
        description: 'Kelola produk/reward yang bisa ditukar dengan poin.',
        steps: [
          'Tambahkan reward baru (produk, diskon, atau voucher)',
          'Set jumlah poin yang dibutuhkan',
          'Set stok reward jika terbatas',
          'Pelanggan bisa redeem dari area member'
        ],
        tips: 'Sediakan reward mulai dari poin kecil agar pelanggan baru sudah bisa merasakan manfaatnya.'
      },
      {
        title: 'LP Cekout',
        icon: Rocket,
        route: '/dashboard/landing-pages',
        color: '#EF4444',
        description: 'Buat landing page checkout untuk produk spesifik.',
        steps: [
          'Klik "Buat Landing Page" untuk LP baru',
          'Pilih produk atau bundle yang dijual',
          'Isi headline, deskripsi, dan benefit',
          'Tambahkan testimoni pelanggan',
          'Upload gambar/video produk',
          'Aktifkan fitur: countdown timer, FAQ, guarantee, social proof',
          'Setiap LP punya URL unik yang bisa dishare',
          'LP juga support produk webinar dengan form registrasi khusus'
        ],
        tips: 'LP checkout memiliki konversi 3-5x lebih tinggi dari storefront biasa karena fokus ke satu produk.'
      },
      {
        title: 'Lead Management',
        icon: Target,
        route: '/dashboard/leads',
        color: '#0EA5E9',
        description: 'CRM sederhana untuk mengelola prospek/leads.',
        steps: [
          'Leads masuk otomatis dari form di website atau manual input',
          'Set status lead: New, Contacted, Qualified, Closed',
          'Tambahkan catatan/notes ke setiap lead',
          'Assign lead ke staff tertentu',
          'Track konversi dari lead ke pelanggan'
        ],
        tips: 'Follow-up lead dalam 5 menit pertama memiliki peluang closing 10x lebih besar.'
      },
      {
        title: 'Atur Lead',
        icon: Sliders,
        route: '/dashboard/lead-settings',
        color: '#6366F1',
        description: 'Konfigurasi pipeline dan sumber lead.',
        steps: [
          'Buat pipeline stages sesuai proses penjualan Anda',
          'Definisikan sumber lead (Facebook, Instagram, WhatsApp, dll)',
          'Set auto-assign rules untuk distribusi lead ke tim',
          'Konfigurasi scoring rules untuk prioritas lead'
        ],
        tips: 'Buat pipeline yang simpel: New → Contacted → Negotiation → Closed Won/Lost.'
      },
      {
        title: 'Segmentasi',
        icon: Users,
        route: '/dashboard/segments',
        color: '#8B5CF6',
        description: 'Kelompokkan pelanggan berdasarkan kriteria tertentu.',
        steps: [
          'Buat segment baru (misal: "Pembeli VIP", "Dormant")',
          'Set aturan: total belanja > X, frekuensi beli > N, dll',
          'Segment otomatis terupdate sesuai aktivitas pelanggan',
          'Gunakan segment sebagai target broadcast atau promo khusus'
        ],
        tips: 'Segment "pelanggan tidak belanja 30 hari" sangat efektif untuk campaign win-back.'
      },
    ]
  },
  {
    id: 'store',
    title: '🏪 Toko & Website',
    description: 'Kelola tampilan toko, halaman, domain, dan pengiriman',
    items: [
      {
        title: 'Store Builder',
        icon: Store,
        route: '/dashboard/store-builder',
        color: '#3B82F6',
        description: 'Kustomisasi tampilan storefront toko online Anda.',
        steps: [
          'Pilih warna utama toko (brand color)',
          'Upload logo dan favicon',
          'Edit teks hero section (headline, sub-headline)',
          'Atur tampilan kategori dan produk',
          'Preview tampilan toko sebelum publish'
        ],
        tips: 'Gunakan warna yang konsisten dengan brand Anda di semua channel marketing.'
      },
      {
        title: 'Page Builder',
        icon: Layout,
        route: '/dashboard/page-builder',
        color: '#8B5CF6',
        description: 'Buat halaman kustom (About, Terms, Privacy, dll).',
        steps: [
          'Klik "Buat Halaman" untuk halaman baru',
          'Isi judul dan slug (URL)',
          'Gunakan rich text editor untuk konten',
          'Halaman otomatis bisa diakses di URL-toko/p/[slug]',
          'Cocok untuk: About Us, Kebijakan Privasi, Syarat Ketentuan'
        ],
        tips: 'Setiap toko wajib punya halaman Kebijakan Privasi dan Syarat Ketentuan untuk kepercayaan pelanggan.'
      },
      {
        title: 'Templates',
        icon: Palette,
        route: '/dashboard/templates',
        color: '#EC4899',
        description: 'Pilih template tampilan toko.',
        steps: [
          'Lihat koleksi template yang tersedia',
          'Preview template sebelum menerapkan',
          'Pilih dan aktifkan template yang diinginkan',
          'Template akan langsung diterapkan ke storefront Anda'
        ],
        tips: 'Pilih template yang sesuai dengan jenis produk Anda — minimalis untuk digital, detail untuk fisik.'
      },
      {
        title: 'Whitelabel',
        icon: Tag,
        route: '/dashboard/whitelabel',
        color: '#F59E0B',
        description: 'Kustomisasi branding untuk menghilangkan brand Tokomoe.',
        steps: [
          'Upload logo kustom Anda',
          'Ubah watermark dan footer',
          'Set brand name sendiri',
          'Semua tampilan customer akan menggunakan branding Anda'
        ],
        tips: 'Whitelabel penting untuk brand yang serius — pelanggan tidak akan tahu Anda menggunakan Tokomoe.'
      },
      {
        title: 'Domain',
        icon: Globe,
        route: '/dashboard/domains',
        color: '#059669',
        description: 'Hubungkan domain kustom ke toko Anda.',
        steps: [
          'Masukkan domain yang Anda miliki (misal: www.tokosaya.com)',
          'Arahkan DNS domain ke server Tokomoe (CNAME/A record)',
          'Tunggu propagasi DNS (biasanya 1-24 jam)',
          'SSL otomatis aktif setelah domain terverifikasi',
          'Toko Anda bisa diakses via domain kustom'
        ],
        tips: 'Gunakan domain .com atau .id untuk kredibilitas maksimal. Setup Cloudflare untuk CDN gratis.'
      },
      {
        title: 'Pengiriman',
        icon: Truck,
        route: '/dashboard/shipping',
        color: '#F97316',
        description: 'Konfigurasi metode dan ongkos kirim.',
        steps: [
          'Pilih provider: RajaOngkir, Komship, atau Manual',
          'Set alamat asal pengiriman (gudang/toko)',
          'Aktifkan gratis ongkir jika minimum belanja tercapai',
          'Atau set ongkir flat rate untuk kemudahan',
          'Konfigurasi ekspedisi yang tersedia: JNE, JNT, SiCepat, dll'
        ],
        tips: 'Gratis ongkir dengan minimum belanja Rp 100-200rb sangat efektif mendorong belanja lebih banyak.'
      },
    ]
  },
  {
    id: 'cms',
    title: '📝 CMS',
    description: 'Blog, FAQ, dan social media',
    items: [
      {
        title: 'Blog',
        icon: FileText,
        route: '/dashboard/blog',
        color: '#3B82F6',
        description: 'Tulis dan kelola artikel blog untuk SEO dan konten marketing.',
        steps: [
          'Klik "Tulis Artikel" untuk artikel baru',
          'Isi judul, konten, dan featured image',
          'Set kategori dan tag untuk organisasi',
          'Publish atau simpan sebagai draft',
          'Artikel tampil di halaman blog storefront',
          'Share link artikel untuk traffic tambahan'
        ],
        tips: 'Posting 2-4 artikel per bulan yang relevan dengan produk Anda untuk SEO jangka panjang.'
      },
      {
        title: 'FAQ',
        icon: HelpCircle,
        route: '/dashboard/faq',
        color: '#10B981',
        description: 'Kelola halaman FAQ (Frequently Asked Questions).',
        steps: [
          'Tambahkan pertanyaan dan jawaban',
          'Atur urutan tampil (drag & drop)',
          'Kelompokkan berdasarkan kategori jika banyak',
          'FAQ otomatis tampil di storefront'
        ],
        tips: 'FAQ yang lengkap mengurangi beban customer support hingga 60%.'
      },
      {
        title: 'Social & Widget',
        icon: Sliders,
        route: '/dashboard/store-extras',
        color: '#8B5CF6',
        description: 'Atur link sosial media dan widget toko.',
        steps: [
          'Isi link Instagram, TikTok, YouTube, Facebook, dll',
          'Isi nomor WhatsApp untuk tombol floating chat',
          'Aktifkan/nonaktifkan widget tertentu',
          'Link sosial media tampil di footer storefront'
        ],
        tips: 'Tombol WhatsApp floating sangat meningkatkan engagement — pelanggan bisa langsung bertanya.'
      },
    ]
  },
  {
    id: 'settings',
    title: '⚙️ Pengaturan',
    description: 'Notifikasi, integrasi, staff, laporan, dan developer tools',
    items: [
      {
        title: 'Pusat Notifikasi',
        icon: Inbox,
        route: '/dashboard/notification-center',
        color: '#3B82F6',
        description: 'Lihat semua notifikasi masuk ke dashboard Anda.',
        steps: [
          'Lihat notifikasi pesanan baru, pembayaran, dll',
          'Klik notifikasi untuk langsung ke detail terkait',
          'Tandai sebagai sudah dibaca',
          'Filter berdasarkan tipe notifikasi'
        ],
        tips: 'Cek notifikasi secara rutin atau aktifkan push notification browser untuk update real-time.'
      },
      {
        title: 'Riwayat Notifikasi',
        icon: History,
        route: '/dashboard/notification-logs',
        color: '#6366F1',
        description: 'Lihat riwayat semua notifikasi yang terkirim ke pelanggan.',
        steps: [
          'Lihat log notifikasi WA dan Email yang terkirim',
          'Filter berdasarkan event, status, atau tanggal',
          'Lihat detail pesan yang terkirim/gagal',
          'Debug jika ada notifikasi yang gagal terkirim'
        ],
        tips: 'Cek log ini jika pelanggan bilang tidak menerima notifikasi — bisa karena nomor salah atau integrasi error.'
      },
      {
        title: 'Template Notifikasi',
        icon: Bell,
        route: '/dashboard/notifications',
        color: '#F59E0B',
        description: 'Kustomisasi template pesan WhatsApp dan Email.',
        steps: [
          'Pilih event (pesanan baru, pembayaran berhasil, dll)',
          'Edit template WhatsApp dan Email terpisah',
          'Gunakan variabel: {customer_name}, {order_number}, {total}, dll',
          'Preview pesan sebelum simpan',
          'Template berlaku untuk semua notifikasi otomatis'
        ],
        tips: 'Tambahkan personality ke template — pesan yang ramah dan personal meningkatkan loyalitas pelanggan.'
      },
      {
        title: 'Integrasi',
        icon: Settings,
        route: '/dashboard/settings',
        color: '#64748B',
        description: 'Hubungkan dengan layanan pihak ketiga.',
        steps: [
          'Setup Responic (WhatsApp API) — untuk kirim pesan otomatis',
          'Setup Mailketing / SMTP — untuk kirim email otomatis',
          'Setup Tripay — untuk payment gateway otomatis',
          'Setup DigiFlazz — untuk PPOB (pulsa, token, dll)',
          'Setup RajaOngkir / Komship — untuk cek ongkir',
          'Setup Facebook Pixel — untuk tracking iklan',
          'Setup Google Analytics — untuk tracking traffic'
        ],
        tips: 'Minimal setup Responic + Tripay untuk operasional dasar. Mailketing untuk email marketing.'
      },
      {
        title: 'Staff & Role',
        icon: UserCog,
        route: '/dashboard/staff',
        color: '#8B5CF6',
        description: 'Kelola akun staff dan hak akses.',
        steps: [
          'Invite staff baru via email',
          'Buat role kustom (misal: Admin, CS, Gudang)',
          'Set permission per role (menu apa saja yang bisa diakses)',
          'Staff login dengan akun sendiri',
          'Lihat aktivitas masing-masing staff'
        ],
        tips: 'Berikan akses minimum yang diperlukan untuk setiap role — prinsip least privilege.'
      },
      {
        title: 'Laporan',
        icon: BarChart3,
        route: '/dashboard/reports',
        color: '#10B981',
        description: 'Laporan penjualan dan bisnis.',
        steps: [
          'Lihat laporan penjualan per periode',
          'Analisis produk dan kategori terlaris',
          'Lihat gross revenue, net revenue, dan margin',
          'Export laporan ke CSV atau PDF'
        ],
        tips: 'Review laporan mingguan untuk membuat keputusan bisnis yang tepat.'
      },
      {
        title: 'Advanced Reports',
        icon: PieChart,
        route: '/dashboard/advanced-reports',
        color: '#7C3AED',
        description: 'Laporan lanjutan dengan segmentasi dan cohort.',
        steps: [
          'Analisis customer lifetime value',
          'Lihat cohort retention pelanggan',
          'Analisis conversion funnel',
          'Breakdown revenue per channel/source'
        ],
        tips: 'Fokus pada metrik yang actionable — conversion rate dan retention lebih penting dari vanity metrics.'
      },
      {
        title: 'Finance Reports',
        icon: TrendingUp,
        route: '/dashboard/finance-reports',
        color: '#059669',
        description: 'Laporan keuangan detail.',
        steps: [
          'Lihat kas masuk dan kas keluar',
          'Analisis profit margin per produk',
          'Lihat rekap per payment method',
          'Track piutang dan hutang',
          'Export untuk kebutuhan pajak/akuntansi'
        ],
        tips: 'Gunakan finance report bulanan sebagai dasar untuk mengisi laporan pajak.'
      },
      {
        title: 'Log Aktivitas',
        icon: Activity,
        route: '/dashboard/activity-logs',
        color: '#F97316',
        description: 'Audit trail semua aksi yang dilakukan di dashboard.',
        steps: [
          'Lihat siapa melakukan apa dan kapan',
          'Filter berdasarkan user, tipe aksi, atau tanggal',
          'Berguna untuk audit keamanan',
          'Track perubahan penting (harga, stok, status order)'
        ],
        tips: 'Review activity log secara berkala terutama jika ada banyak staff yang akses dashboard.'
      },
      {
        title: 'Subscription',
        icon: Sparkles,
        route: '/dashboard/subscription',
        color: '#EC4899',
        description: 'Kelola paket langganan Tokomoe Anda.',
        steps: [
          'Lihat paket saat ini dan fitur yang aktif',
          'Upgrade ke paket lebih tinggi untuk fitur tambahan',
          'Lihat riwayat pembayaran subscription',
          'Kelola metode pembayaran'
        ],
        tips: 'Upgrade ke paket yang sesuai kebutuhan — jangan over-subscribe fitur yang belum Anda butuhkan.'
      },
      {
        title: 'Affiliate SaaS',
        icon: Share2,
        route: '/dashboard/saas-affiliate',
        color: '#10B981',
        description: 'Dapatkan komisi dengan merekomendasikan Tokomoe.',
        steps: [
          'Dapatkan link affiliate unik Anda',
          'Bagikan ke sesama pebisnis online',
          'Dapatkan komisi untuk setiap referral yang berlangganan',
          'Pantau klik, konversi, dan komisi',
          'Ajukan pencairan komisi'
        ],
        tips: 'Rekomendasikan ke komunitas bisnis Anda — testimoni pengalaman pribadi paling meyakinkan.'
      },
      {
        title: 'Import / Export',
        icon: FileText,
        route: '/dashboard/import-export',
        color: '#6366F1',
        description: 'Import dan export data toko.',
        steps: [
          'Export: produk, pesanan, atau pelanggan ke CSV',
          'Import: bulk tambah produk dari file CSV',
          'Download template CSV untuk format yang benar',
          'Cocok untuk migrasi dari platform lain'
        ],
        tips: 'Backup data Anda secara berkala dengan fitur export. Simpan file CSV di tempat yang aman.'
      },
      {
        title: 'API Keys',
        icon: Key,
        route: '/dashboard/api-keys',
        color: '#EF4444',
        description: 'Kelola API key untuk integrasi kustom.',
        steps: [
          'Generate API key baru',
          'Beri nama/label untuk identifikasi',
          'Gunakan API key untuk akses REST API Tokomoe',
          'Revoke key yang sudah tidak dipakai'
        ],
        tips: 'Jangan share API key Anda. Gunakan key berbeda untuk setiap integrasi.'
      },
      {
        title: 'Webhooks',
        icon: Webhook,
        route: '/dashboard/webhooks',
        color: '#F97316',
        description: 'Terima notifikasi real-time ke server Anda.',
        steps: [
          'Tambahkan URL webhook endpoint Anda',
          'Pilih event yang trigger webhook (order, payment, dll)',
          'Tokomoe akan POST data ke URL Anda saat event terjadi',
          'Cocok untuk integrasi dengan sistem lain (ERP, akuntansi, dll)'
        ],
        tips: 'Pastikan endpoint Anda merespon 200 OK dalam 5 detik untuk menghindari retry.'
      },
      {
        title: 'API Docs',
        icon: BookOpen,
        route: '/dashboard/api-docs',
        color: '#0EA5E9',
        description: 'Dokumentasi lengkap REST API Tokomoe.',
        steps: [
          'Lihat semua endpoint API yang tersedia',
          'Baca format request dan response',
          'Coba endpoint langsung dari halaman docs',
          'Gunakan untuk integrasi dengan aplikasi lain'
        ],
        tips: 'API docs sangat berguna jika Anda ingin membangun aplikasi mobile atau integrasi kustom.'
      },
    ]
  },
];

export default function Tutorial() {
  const [search, setSearch] = useState('');
  const [expandedSection, setExpandedSection] = useState('getting-started');
  const [expandedItem, setExpandedItem] = useState(null);

  const filteredSections = sections.map(section => ({
    ...section,
    items: section.items.filter(item =>
      item.title.toLowerCase().includes(search.toLowerCase()) ||
      item.description.toLowerCase().includes(search.toLowerCase()) ||
      item.steps.some(s => s.toLowerCase().includes(search.toLowerCase()))
    )
  })).filter(s => s.items.length > 0);

  const totalModules = sections.reduce((acc, s) => acc + s.items.length, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-4">
            <BookOpen size={16} /> Panduan Lengkap
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 mb-2">Tutorial Dashboard Tokomoe</h1>
          <p className="text-slate-500 max-w-2xl mx-auto">
            Panduan lengkap untuk semua {totalModules} modul dan fitur di dashboard. Klik modul untuk melihat langkah-langkah penggunaan.
          </p>
        </div>

        {/* Search */}
        <div className="relative max-w-lg mx-auto mb-8">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Cari modul atau fitur... (misal: voucher, webinar, pengiriman)"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 h-12 text-base rounded-xl border-slate-200 shadow-sm"
          />
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Total Modul', value: totalModules, color: '#3B82F6' },
            { label: 'Kategori', value: sections.length, color: '#10B981' },
            { label: 'Integrasi', value: '7+', color: '#F59E0B' },
            { label: 'Gratis Tutorial', value: '✓', color: '#8B5CF6' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 text-center shadow-sm">
              <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
              <p className="text-xs text-slate-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {filteredSections.map(section => (
            <div key={section.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Section Header */}
              <button
                onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{section.title.split(' ')[0]}</span>
                  <div className="text-left">
                    <h2 className="text-lg font-bold text-slate-900">{section.title.split(' ').slice(1).join(' ')}</h2>
                    <p className="text-sm text-slate-500">{section.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="text-xs">{section.items.length} modul</Badge>
                  {expandedSection === section.id ? <ChevronDown size={20} className="text-slate-400" /> : <ChevronRight size={20} className="text-slate-400" />}
                </div>
              </button>

              {/* Section Items */}
              {expandedSection === section.id && (
                <div className="border-t border-slate-100 divide-y divide-slate-100">
                  {section.items.map((item, idx) => {
                    const Icon = item.icon;
                    const isExpanded = expandedItem === `${section.id}-${idx}`;
                    return (
                      <div key={idx}>
                        <button
                          onClick={() => setExpandedItem(isExpanded ? null : `${section.id}-${idx}`)}
                          className="w-full flex items-center gap-4 px-6 py-3.5 hover:bg-slate-50/70 transition-colors"
                        >
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${item.color}15` }}>
                            <Icon size={18} style={{ color: item.color }} />
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-slate-800">{item.title}</h3>
                            </div>
                            <p className="text-sm text-slate-500 truncate">{item.description}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-200">{item.steps.length} langkah</Badge>
                            {isExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                          </div>
                        </button>

                        {/* Expanded detail */}
                        {isExpanded && (
                          <div className="px-6 pb-5 pt-1 ml-[52px]">
                            <div className="bg-slate-50 rounded-xl p-5 space-y-4">
                              {/* Steps */}
                              <div>
                                <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                                  <span className="w-5 h-5 rounded bg-blue-500 text-white text-[10px] flex items-center justify-center font-bold">?</span>
                                  Cara Penggunaan
                                </h4>
                                <ol className="space-y-2">
                                  {item.steps.map((step, si) => (
                                    <li key={si} className="flex items-start gap-3 text-sm">
                                      <span className="w-6 h-6 rounded-full bg-white border-2 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5" style={{ borderColor: item.color, color: item.color }}>
                                        {si + 1}
                                      </span>
                                      <span className="text-slate-700 leading-relaxed pt-0.5">{step}</span>
                                    </li>
                                  ))}
                                </ol>
                              </div>

                              {/* Tips */}
                              {item.tips && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                                  <span className="text-amber-500 text-lg leading-none mt-0.5">💡</span>
                                  <p className="text-sm text-amber-800">{item.tips}</p>
                                </div>
                              )}

                              {/* Go to page button */}
                              <a
                                href={item.route}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white shadow-sm transition-all hover:opacity-90"
                                style={{ backgroundColor: item.color }}
                              >
                                <Icon size={14} /> Buka {item.title}
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredSections.length === 0 && (
          <div className="text-center py-16">
            <Search size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 font-medium">Tidak ada modul yang cocok dengan "{search}"</p>
            <p className="text-sm text-slate-400 mt-1">Coba kata kunci lain</p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-10 text-sm text-slate-400">
          <p>Tokomoe Dashboard Tutorial • {totalModules} Modul • {sections.length} Kategori</p>
          <p className="mt-1">Butuh bantuan lebih? Hubungi tim support kami via WhatsApp.</p>
        </div>
      </div>
    </div>
  );
}
